import { useState } from "react";
import { Link } from "react-router-dom";
import { InstagramLogo, DribbbleLogo, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're subscribed!");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const cols = [
    { title: "Company", links: [["About", "/about"], ["Pricing", "/pricing"], ["Contact", "/contact"]] },
    { title: "Services", links: [["Web Design", "/services"], ["E-commerce", "/services"], ["SEO", "/services"], ["Branding", "/services"]] },
    { title: "Program", links: [["Referral Program", "/referral"], ["Free Consultation", "/contact"], ["Admin", "/admin"]] },
  ];

  return (
    <footer data-testid="footer" className="bg-black text-white">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-display text-3xl font-black tracking-tighter">Kenny Web Design</div>
            <p className="mt-4 text-zinc-400 max-w-sm leading-relaxed">
              Modern websites that help businesses grow. Custom-built, mobile-first, and engineered to convert.
            </p>
            <form onSubmit={subscribe} className="mt-8 max-w-sm" data-testid="newsletter-form">
              <label className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Newsletter</label>
              <div className="mt-2 flex items-center border-b border-zinc-700 focus-within:border-white transition-colors">
                <input
                  data-testid="newsletter-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.com"
                  className="flex-1 bg-transparent py-3 outline-none placeholder:text-zinc-600 text-white"
                />
                <button data-testid="newsletter-submit" disabled={loading} className="p-2 hover:text-zinc-300 disabled:opacity-50">
                  <ArrowRight size={20} />
                </button>
              </div>
            </form>
          </div>

          {cols.map((c) => (
            <div key={c.title} className="lg:col-span-2">
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-4">{c.title}</div>
              <ul className="space-y-3">
                {c.links.map(([label, to]) => (
                  <li key={label}>
                    <Link to={to} className="text-zinc-300 hover:text-white transition-colors text-sm">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-1">
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Social</div>
            <div className="flex lg:flex-col gap-4 text-zinc-300">
              <a href="https://instagram.com/kennywebdesign" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-white transition-colors"><InstagramLogo size={22} /></a>
              <a href="#" aria-label="Dribbble" className="hover:text-white transition-colors"><DribbbleLogo size={22} /></a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-zinc-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-zinc-500 text-sm">© {new Date().getFullYear()} Kenny Web Design. All rights reserved.</p>
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
